import { useCallback, useEffect, useState, type Dispatch, type SetStateAction, type DependencyList } from 'react';

export interface AsyncData<T> {
  /** null — пока грузится (или пока загрузка не нужна). */
  data: T | null;
  error: string | null;
  /** Перезапросить с теми же зависимостями — после сохранения/удаления. */
  reload: () => void;
  /** Правка загруженного на месте — там, где ответ сервера уже известен и перезапрос не нужен. */
  setData: Dispatch<SetStateAction<T | null>>;
}

/**
 * Загрузка данных страницы: запрос на изменение зависимостей, сброс в null на
 * время загрузки и отмена устаревшего ответа.
 *
 * Заменяет один и тот же useEffect с флагом `cancelled`, который был скопирован
 * почти во все страницы: без отмены ответ по старым фильтрам мог прийти позже
 * нового и перезаписать список.
 *
 * `load === null` — загружать нечего (нет id в маршруте, открыта форма вместо
 * списка): запрос не уходит, прежние данные остаются на месте.
 *
 * `errorMessage === null` — ошибка проглатывается: так сделаны необязательные
 * справочники, без которых страница всё равно работает.
 */
export function useAsyncData<T>(
  load: (() => Promise<T>) | null,
  deps: DependencyList,
  errorMessage: string | null = null,
): AsyncData<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (!load) return;

    let cancelled = false;
    setData(null);
    setError(null);

    load()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled && errorMessage) setError(errorMessage);
      });

    return () => {
      cancelled = true;
    };
    // Зависимости задаёт вызывающая сторона: сам `load` — новая функция на
    // каждый рендер, в списке ей делать нечего.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadToken]);

  return { data, error, reload, setData };
}
